/**
 * GH-877: the small window a framed give box opens for a donor to sign in —
 * `/embed/give/<organizationId>/sign-in#n=<nonce>`.
 *
 * It is a top-level GiveHere page, so the one sign-in sheet every donor page uses (`SignInSheet`:
 * Apple, Google, phone) works here as it does on the give page, and the device is remembered as it
 * is there. Once a session exists it is handed to the box that opened this window — `postMessage`
 * to `window.opener`, addressed to **this origin only** (a page on another origin that opened this
 * window itself receives nothing), carrying the nonce from the address (so the box takes only the
 * answer to its own press) — and the window closes. Opened without an opener, or without a nonce,
 * it simply says the donor is signed in.
 *
 * The nonce is read once and the fragment cleared, so it does not linger in the address bar.
 * Never framable: the static server sends `frame-ancestors 'none'` for every `/embed/` path but
 * the give box itself.
 */
import { EMBED_WIDGET_COPY } from "@givehere/contracts";
import { useState } from "react";
import { SignInSheet } from "../SignInSheet.js";
import { signInNonceFromHash, type SignedInMessage } from "./embed-frame.js";
import "./embed.css";

const COPY = EMBED_WIDGET_COPY.donor;

/** Read once per page load (StrictMode may run an initializer twice), then cleared. */
let taken: { readonly nonce: string | null } | null = null;

function takeNonce(): string | null {
  if (taken) return taken.nonce;
  taken = { nonce: signInNonceFromHash(window.location.hash) };
  if (window.location.hash) {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
  return taken.nonce;
}

/** Tests only: forget the nonce read for the previous render. */
export function resetTakenNonceForTests(): void {
  taken = null;
}

export function EmbedSignIn() {
  const [nonce] = useState(takeNonce);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <main className="embed-signin">
        <p className="embed__note" role="status">
          {COPY.windowDone}
        </p>
      </main>
    );
  }
  return (
    <main className="embed-signin">
      <SignInSheet
        heading={COPY.windowHeading}
        lede={COPY.windowLede}
        onIssued={(issued) => {
          const opener = window.opener as Window | null;
          if (opener && nonce) {
            const answer: SignedInMessage = {
              type: "givehere:embed-signed-in",
              nonce,
              token: issued.token,
            };
            opener.postMessage(answer, window.location.origin);
            window.close();
          }
          setDone(true);
        }}
        onDismiss={() => window.close()}
      />
    </main>
  );
}
